import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { GoPrimitiveDot } from "react-icons/go";
import Swal from "sweetalert2";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { APP_CONTEXT } from "../../../utils/context";
import PageHeader from "../../../components/PageHeader/PageHeader.component";
import { Row, Column } from "../../../components/Grid/Grid.component";
import Form from "./UserType.form.component";
import Button from "../../../components/Button/Button.component";
import Error from "../../../components/Error/ErrorMessage.component";
import BorderSelect from "../../../components/Form/BorderSelect/BorderSelect.component";
import Notification from "../../../components/Toast/Toast.component";
import { PermissionsList } from "./permissionsList";

// GQL
import {
  UPDATE_USER_TYPE,
  DELETE_USER_TYPE,
} from "../../../graphql/mutations/userType.mutation";
import {
  GET_USER_ROLE,
  GET_AFFECTED_USERS,
} from "../../../graphql/queries/userType.query";
import { formatGraphqlError } from "../../../utils/api";

const UserTypeEdit = () => {
  const { setActiveSideNav, setPageTitle } = useContext(APP_CONTEXT);
  const [userTypeData, setUserTypeData] = useState({
    id: "",
    title: "",
    description: "",
    status: "ACTIVE",
    permissions: PermissionsList,
  });
  const [updateUserType, { loading }] = useMutation(UPDATE_USER_TYPE);
  const [deleteUserType] = useMutation(DELETE_USER_TYPE);
  const { id } = useParams();
  const userType = useQuery(GET_USER_ROLE, {
    fetchPolicy: "no-cache",
    variables: { id },
  });

  const affectedUsers = useQuery(GET_AFFECTED_USERS, {
    fetchPolicy: "no-cache",
    variables: { id },
  });
  const { data } = userType;
  const history = useHistory();

  useEffect(() => {
    setActiveSideNav("sysConf");
    setPageTitle("Manage User Type");
  });

  const { register, handleSubmit, errors, setValue, getValues } = useForm();

  const changeStatus = (e) => {
    const tempUserTypeData = { ...userTypeData };
    tempUserTypeData.status = e.target.value;
    setUserTypeData(tempUserTypeData);
  };

  const getColor = () => {
    switch (userTypeData.status) {
      case "ACTIVE":
        return "#39C568";
      case "ARCHIVE":
        return "#FF003A";
      case "DRAFT":
        return "#707172";
      default:
        return "#707172";
    }
  };

  const getAffectedUsersHtml = () => {
    const affected = affectedUsers.data.adminUsers.map((user) => {
      return `${user.firstName} ${user.lastName}`;
    });
    return affected.join("<br/>");
  };

  const onSubmit = (values) => {
    /**
     * Create new User Type
     * push to list all user types
     */

    const submitValues = values;
    submitValues.id = userTypeData.id;

    Swal.fire({
      title: "Are you sure?",
      html: `The following people will be affected:<br/>${getAffectedUsersHtml()}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    }).then((result) => {
      if (result.value) {
        updateUserType({
          variables: {
            ...submitValues,
          },
        })
          .then(() => {
            Notification.success({
              title: "Saved successfully",
              message: "You updated a user type",
            });
            history.push("/app/system-configuration/user-types");
          })
          .catch((err) =>
            Notification.error({
              title: "Create User Type Failed",
              message: formatGraphqlError(err),
            })
          );
      }
    });
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.value) {
        deleteUserType({
          variables: {
            id: userTypeData.id,
          },
        })
          .then(() => {
            Swal.fire(
              "Deleted!",
              "User Type has been deleted.",
              "success"
            ).then(() => {
              history.push("/app/system-configuration/user-types");
            });
          })
          .catch((err) =>
            Notification.error({
              title: "Delete User Type Failed",
              message: formatGraphqlError(err),
            })
          );
      }
    });
  };

  if (userType.loading) {
    return "Loading...";
  }

  return (
    <>
      <PageHeader title={`Edit User Type: ${userTypeData.title}`} back />
      <Row>
        <Column width="70%" noPad>
          <Form
            register={register}
            errors={errors}
            userTypeData={userTypeData}
            setUserTypeData={setUserTypeData}
            userTypeEditData={data && data.userRole}
            setValue={setValue}
            getValues={getValues}
          />
        </Column>
        <Column width="30%">
          <BorderSelect
            header="Status"
            name="status"
            label="Please select a status"
            value={userTypeData.status}
            register={register}
            onChange={changeStatus}
            options={[
              { value: "ACTIVE", text: "Active" },
              { value: "ARCHIVE", text: "Archive" },
              { value: "DRAFT", text: "Draft" },
            ]}
            icon={
              <GoPrimitiveDot style={{ color: getColor(), fontSize: "25px" }} />
            }
            margin="60px 0px 120px 0px"
            required={{
              required: "please enter a User Type ID",
              pattern: {
                value: /^[a-zA-Z]+$/,
                message: "field cannot be empty",
              },
            }}
            error={errors.id && <Error message={errors.id.message} />}
          />

          <Button
            width="100%"
            onClick={() => handleDelete()}
            type="button"
            value="Archive"
            loading={loading}
          />
          <Button
            width="100%"
            onClick={handleSubmit(onSubmit)}
            type="button"
            kind="secondary"
            value="Save & Update"
            loading={loading}
          />
        </Column>
      </Row>
    </>
  );
};

export default UserTypeEdit;
